/** Hook that derives display data from stores for MasterLayout. */

import { useMemo, useCallback, useEffect, useState } from "react";
import { getFrameTypeIcon } from "@/components/ui/concept-icons";
import { useProjectStore } from "@/stores/project-store";
import { useCanvasStore } from "@/stores/canvas-store";
import { useSelectionStore } from "@/stores/selection-store";
import { frameNodeHidden } from "@/stores/canvas-frame-geometry";
import { useFramesStore } from "@/stores/frames-store";
import {
  isFrameHiddenByMetadata,
  selectOrderedFrames,
} from "@/stores/frames-selectors";
import { useCompanionStore } from "@/stores/companion-store";
import type { ExecutionStatus } from "@/stores/companion-store";
import { useAgentStore } from "@/stores/agent-store";
import type { FrameEntry, TaskEntry } from "@/components/panels/left-sidebar";
import type { BrowseProject } from "@/components/panels/browse-tree";
import type {
  ExecutionSidebarProps,
  ExecutionState,
} from "@/components/panels/execution-sidebar";
import type { FrameType } from "@/api/spaces";
import * as spacesApi from "@/api/spaces";
import { abandonOnboardingOnUserSpaceSwitch } from "@/desktop/onboarding/abandon-onboarding-on-switch";
import { activateFrameInCurrentContext } from "@/lib/focus-frame-activation";
import { newTextFrameFields } from "@/components/shapes/doc-frame/new-text-frame";
import type { FrameData } from "@/lib/frame-data";
import { ingestFrame } from "@/lib/frame-ingest";
import { setFrameHidden } from "@/lib/frame-visibility";

// Agent colors now come from template.color via the API.
// agentColorByIndex is the fallback for templates without a color yet.
import { agentColorByIndex } from "@/lib/agent-colors";
import { translate, useT } from "@/i18n";
import { layoutMessages } from "@/i18n/messages/layout";

const FRAME_TYPE_COLORS: Record<FrameType, string> = {
  markdown: "bg-frame-markdown",
  html: "bg-frame-html",
  question: "bg-frame-question",
  code: "bg-frame-code",
  search: "bg-frame-search",
  workflow: "bg-frame-workflow",
  image: "bg-frame-image",
  email: "bg-frame-email",
  newsletter: "bg-frame-newsletter",
  connection: "bg-frame-connection",
  connection_prompt: "bg-frame-connection",
  memory: "bg-frame-memory",
  note: "bg-frame-note",
  doc: "bg-frame-note",
  conversation: "bg-frame-conversation",
  linkedin: "bg-frame-linkedin",
  agent_list: "bg-frame-workflow",
  agent_settings: "bg-frame-workflow",
  taskboard: "bg-accent",
  github: "bg-frame-code",
  event: "bg-frame-email",
  agenda: "bg-frame-newsletter",
  flight: "bg-frame-email",
  weather: "bg-frame-search",
  chart: "bg-frame-data",
  dataset: "bg-frame-data",
  preview_card: "bg-frame-image",
  link_preview: "bg-frame-html",
  chat: "bg-frame-conversation",
  slack: "bg-frame-linkedin",
  todo: "bg-frame-note",
  board: "bg-accent",
  music: "bg-frame-newsletter",
  calendar: "bg-frame-email",
  file: "bg-frame-note",
  terminal: "bg-frame-code",
};

/** Map frame status to a status dot color class. */
function frameStatusColor(status: string): string {
  switch (status) {
    case "streaming":
      return "bg-status-selected"; // blue — running
    case "error":
      return "bg-status-error"; // red — failed
    default:
      return "bg-status-streaming"; // green — idle & done are both "delivered"
  }
}

function buildSidebarFrames(
  frames: readonly FrameData[],
  focusHiddenFrameIds: ReadonlySet<string>,
  selectedFrameId: string | null,
): FrameEntry[] {
  return frames.map((frame) => {
    const title = sidebarFrameTitle(frame);
    return {
      id: frame.frameId,
      title: title || translate(layoutMessages, "untitled"),
      icon: getFrameTypeIcon(frame.frameType),
      accentColor: FRAME_TYPE_COLORS[frame.frameType] ?? "bg-accent",
      frameType: frame.frameType,
      active: frame.frameId === selectedFrameId,
      statusColor: frameStatusColor(frame.status),
      hidden: frameNodeHidden(frame, focusHiddenFrameIds),
    };
  });
}

function sidebarFrameTitle(frame: FrameData): string {
  if (frame.title) return frame.title;
  if (frame.frameType === "note" || frame.frameType === "doc") {
    // V1 notes and Docs (V4 snapshot) both keep their plain text in `text`.
    const text = (frame.content?.text as string) ?? "";
    return text.trim()
      ? text.trim().split(/\s+/).slice(0, 6).join(" ").slice(0, 60)
      : translate(layoutMessages, "note");
  }
  return "";
}

function sidebarFramesSignature(
  frames: readonly FrameData[],
  focusHiddenFrameIds: ReadonlySet<string>,
  selectedFrameId: string | null,
): string {
  return frames
    .map((frame) =>
      [
        frame.frameId,
        sidebarFrameTitle(frame),
        frame.frameType,
        frame.status,
        frameNodeHidden(frame, focusHiddenFrameIds) ? "1" : "0",
        frame.frameId === selectedFrameId ? "1" : "0",
      ].join("\u001f"),
    )
    .join("\u001e");
}

/** The sidebar's frames: frame data plus the canvas's focus-mode hiding. */
function sidebarFrameSources(): [FrameData[], ReadonlySet<string>] {
  return [
    selectOrderedFrames(useFramesStore.getState()),
    useCanvasStore.getState().focusHiddenFrameIds,
  ];
}

export function useMasterLayoutData() {
  const spaces = useProjectStore((s) => s.spaces);
  const currentSpace = useProjectStore((s) => s.currentSpace);
  const currentProject = useProjectStore((s) => s.currentProject);
  const setCurrentSpace = useProjectStore((s) => s.setCurrentSpace);
  const allProjectSpaces = useProjectStore((s) => s.allProjectSpaces);
  const expandedProjectIds = useProjectStore((s) => s.expandedProjectIds);
  const projects = useProjectStore((s) => s.projects);
  const toggleProjectExpanded = useProjectStore((s) => s.toggleProjectExpanded);
  const renameProject = useProjectStore((s) => s.renameProject);
  const updateProjectColor = useProjectStore((s) => s.updateProjectColor);
  const toggleProjectPinned = useProjectStore((s) => s.toggleProjectPinned);
  const deleteProjectAction = useProjectStore((s) => s.deleteProject);
  const renameSpace = useProjectStore((s) => s.renameSpace);
  const updateSpaceColor = useProjectStore((s) => s.updateSpaceColor);
  const toggleSpacePinned = useProjectStore((s) => s.toggleSpacePinned);
  const createProjectWithDefaults = useProjectStore(
    (s) => s.createProjectWithDefaults,
  );
  const fetchAllProjects = useProjectStore((s) => s.fetchAllProjects);

  const selectedFrameId = useSelectionStore((s) => s.selectedFrameId);
  const selectFrame = useCanvasStore((s) => s.selectFrame);

  const companionStatus = useCompanionStore((s) => s.status);
  const executions = useCompanionStore((s) => s.executions);
  const currentExecutionId = useCompanionStore((s) => s.currentExecutionId);
  const isAgentResponding = useCompanionStore((s) => s.isAgentResponding);

  const projectAgents = useAgentStore((s) => s.agents);
  const selectedAgentId = useAgentStore((s) => s.selectedAgentId);
  const selectAgent = useAgentStore((s) => s.selectAgent);
  const fetchProjectAgents = useAgentStore((s) => s.fetchProjectAgents);
  const [selectedAgentIds, setSelectedAgentIds] = useState<Set<string>>(
    new Set(),
  );

  // When project changes, load its agents. Denker's chat, run and cursors
  // stay: the owner connection's switch keeps what is Denker's.
  useEffect(() => {
    if (currentProject) fetchProjectAgents(currentProject.id);
  }, [currentProject, fetchProjectAgents]);

  useEffect(() => {
    fetchAllProjects();
  }, [fetchAllProjects]);

  const [sidebarFrames, setSidebarFrames] = useState<FrameEntry[]>(() =>
    buildSidebarFrames(...sidebarFrameSources(), selectedFrameId),
  );
  useEffect(() => {
    let lastSignature = sidebarFramesSignature(
      ...sidebarFrameSources(),
      selectedFrameId,
    );
    setSidebarFrames(
      buildSidebarFrames(...sidebarFrameSources(), selectedFrameId),
    );
    const refresh = () => {
      const sources = sidebarFrameSources();
      const nextSignature = sidebarFramesSignature(...sources, selectedFrameId);
      if (nextSignature === lastSignature) return;
      lastSignature = nextSignature;
      setSidebarFrames(buildSidebarFrames(...sources, selectedFrameId));
    };
    const unsubscribeFrames = useFramesStore.subscribe(refresh);
    const unsubscribeCanvas = useCanvasStore.subscribe((state, previous) => {
      if (state.focusHiddenFrameIds !== previous.focusHiddenFrameIds) {
        refresh();
      }
    });
    return () => {
      unsubscribeFrames();
      unsubscribeCanvas();
    };
  }, [selectedFrameId]);

  // Derive sidebar tasks from the current execution's todos
  const sidebarTasks: TaskEntry[] = useMemo(() => {
    if (!currentExecutionId) return [];
    const exec = executions.get(currentExecutionId);
    if (!exec) return [];

    return exec.todos.map((t) => ({
      id: t.id,
      title: t.title,
      checked: t.status === "completed",
    }));
  }, [currentExecutionId, executions]);

  const browseProjects: BrowseProject[] = useMemo(() => {
    return projects.map((p) => ({
      id: p.id,
      name: p.name,
      color: p.color || "#30D158",
      isHome: Boolean(p.is_home),
      isPinned: p.is_pinned,
      expanded: expandedProjectIds.has(p.id),
      spaces: (allProjectSpaces[p.id] ?? []).map((sp) => ({
        id: sp.id,
        name: sp.name,
        isPinned: sp.is_pinned,
        active: sp.id === currentSpace?.id,
      })),
    }));
  }, [projects, allProjectSpaces, expandedProjectIds, currentSpace]);

  const switcherProjects = useMemo(
    () =>
      projects.map((p) => ({
        id: p.id,
        name: p.name,
        color: p.color || "#30D158",
        active: p.id === currentProject?.id,
        isHome: Boolean(p.is_home),
        isPinned: p.is_pinned,
        spaces: (allProjectSpaces[p.id] ?? []).map((sp) => ({
          id: sp.id,
          label: sp.name,
          active: sp.id === currentSpace?.id,
          color: sp.color,
          isPinned: sp.is_pinned,
        })),
      })),
    [projects, allProjectSpaces, currentProject, currentSpace],
  );

  const handleSelectFrame = useCallback((frameId: string) => {
    activateFrameInCurrentContext(frameId);
  }, []);

  const handleBrowseSelectSpace = useCallback(
    (projectId: string, spaceId: string) => {
      const proj = projects.find((p) => p.id === projectId);
      const sp = (allProjectSpaces[projectId] ?? []).find(
        (s) => s.id === spaceId,
      );
      if (!proj || !sp) return;
      abandonOnboardingOnUserSpaceSwitch(sp.id);
      // Atomic switch so the canvas useEffect sees currentSpaceId change once
      // (old → new), not old → null → new. The null intermediate caused a WS
      // teardown+reconnect flap and /auth/user/ polling storm → 429.
      if (currentProject?.id !== projectId) {
        useProjectStore.getState().selectProjectSpace(proj, sp);
      } else {
        setCurrentSpace(sp);
      }
    },
    [projects, allProjectSpaces, currentProject, setCurrentSpace],
  );

  const handleAddFrame = useCallback(async () => {
    if (!currentProject || !currentSpace) return;

    // Position omitted — backend slots the new note into the control
    // area via _next_frame_position(frame_type="note").
    try {
      const frame = await spacesApi.createFrame(
        currentProject.id,
        currentSpace.id,
        {
          ...newTextFrameFields(),
          title: "",
          width: 400,
          height: 300,
        },
      );
      ingestFrame(frame);
      selectFrame(frame.id);
      requestAnimationFrame(() => {
        activateFrameInCurrentContext(frame.id);
      });
    } catch {
      // API failure — frame won't appear on canvas
    }
  }, [currentProject, currentSpace, selectFrame]);

  // Execution todos are read-only (driven by Claude Code)
  const handleToggleTask = useCallback((_taskId: string) => {}, []);

  const handleToggleFrameVisibility = useCallback((frameId: string) => {
    // Toggle the DURABLE flag (metadata.hidden), not the effective
    // node.hidden — the latter can also be true from ephemeral
    // focus-mode hiding, which must not be persisted.
    const frame = useFramesStore.getState().framesById[frameId];
    if (!frame) return;
    setFrameHidden(frameId, !isFrameHiddenByMetadata(frame));
  }, []);

  const handleSpaceSelect = useCallback(
    (spaceId: string) => {
      const sp = spaces.find((s) => s.id === spaceId);
      if (!sp) return;
      abandonOnboardingOnUserSpaceSwitch(sp.id);
      setCurrentSpace(sp);
    },
    [spaces, setCurrentSpace],
  );

  const tLayout = useT(layoutMessages);
  const inputAgents = useMemo(
    () =>
      projectAgents
        .map((pa, i) => ({
          id: pa.id,
          agentTemplate: pa.agent_template,
          name: pa.template_name || tLayout("agent"),
          color: pa.template_color || agentColorByIndex(i),
          avatar_seed: pa.template_avatar_seed || pa.template_name || "Agent",
          isBuiltin: pa.is_template_builtin ?? false,
        }))
        .sort((a, b) => {
          if (a.isBuiltin && !b.isBuiltin) return -1;
          if (!a.isBuiltin && b.isBuiltin) return 1;
          return a.name.localeCompare(b.name);
        }),
    [projectAgents, tLayout],
  );

  // Auto-select first agent + sync Set-based selectedAgentIds with store
  useEffect(() => {
    const first = inputAgents[0];
    if (!selectedAgentId && first) selectAgent(first.id);
    if (selectedAgentId) {
      setSelectedAgentIds((prev) =>
        prev.size === 1 && prev.has(selectedAgentId)
          ? prev
          : new Set([selectedAgentId]),
      );
    }
  }, [selectedAgentId, inputAgents, selectAgent]);

  const handleSelectAgent = useCallback(
    (agentId: string | null) => {
      const prev = useAgentStore.getState().selectedAgentId;
      selectAgent(agentId);
      // Switching to a different agent: clear execution context so the next
      // message starts a fresh session with the new agent, and remove stale
      // cursors from the previous agent.
      if (agentId && prev && agentId !== prev) {
        useCompanionStore.getState().setCurrentExecution(null);
        useCanvasStore.getState().clearAgentCursors();
      }
    },
    [selectAgent],
  );

  const handleSelectAgents = useCallback(
    (ids: Set<string>) => {
      setSelectedAgentIds(ids);
      const first = ids.size > 0 ? [...ids][0]! : null;
      const prev = useAgentStore.getState().selectedAgentId;
      if (first !== prev) {
        selectAgent(first);
        // Clear execution context so the next message starts a fresh session
        // with the new agent instead of routing through the old agent's session.
        if (first && prev) {
          useCompanionStore.getState().setCurrentExecution(null);
          useCanvasStore.getState().clearAgentCursors();
        }
      }
    },
    [selectAgent],
  );

  const executionProps = useMemo((): ExecutionSidebarProps | undefined => {
    if (!currentExecutionId) return undefined;
    const exec = executions.get(currentExecutionId);
    if (!exec) return undefined;
    const stateMap: Record<ExecutionStatus, ExecutionState> = {
      queued: "idle",
      running: "running",
      completed: "completed",
      failed: "failed",
      cancelled: "failed",
      waiting_approval: "approval",
      paused: "idle",
    };
    return {
      executionState: stateMap[exec.status] ?? "idle",
      isAgentResponding,
      todos: exec.todos,
    };
  }, [currentExecutionId, executions, isAgentResponding]);

  return {
    currentProject,
    currentSpace,
    companionStatus,
    selectedFrameId,
    selectFrame,
    handleSelectFrame,
    browseProjects,
    handleBrowseSelectSpace,
    toggleProjectExpanded,
    renameProject,
    updateProjectColor,
    toggleProjectPinned,
    deleteProject: deleteProjectAction,
    renameSpace,
    updateSpaceColor,
    toggleSpacePinned,
    createProjectWithDefaults,
    sidebarFrames,
    sidebarTasks,
    switcherProjects,
    handleAddFrame,
    handleToggleTask,
    handleToggleFrameVisibility,
    handleSpaceSelect,
    inputAgents,
    selectedAgentId,
    selectedAgentIds,
    handleSelectAgent,
    handleSelectAgents,
    executionProps,
  };
}

export type MasterLayoutData = ReturnType<typeof useMasterLayoutData>;
